/**
 * Titration-curve helpers (session 342B). Pure functions used by
 * the live pH-vs-volume HUD overlay (`PhTitrationCurve.tsx`) and
 * exercised in isolation by `tests/l4-titration-curve.test.ts`.
 *
 * The component itself reads the per-vessel `TimeSeries` snapshot
 * surfaced through the inspection callback (Stage → App → overlay).
 * Each sample carries `(t, pH, volumeMl)`; this module flattens that
 * into a `(volumeMl, pH)` curve and finds the equivalence point as
 * the largest dpH/dV jump — the standard pedagogical definition for
 * an acid-base titration where the curve is vertical at endpoint.
 */
/**
 * Detect the equivalence point on a (volume, pH) curve.
 *
 * Algorithm:
 *   1. Drop samples whose pH is null OR whose volume did not advance
 *      since the previous sample (avoids 1/0 explosions).
 *   2. For each consecutive pair, compute |Δ pH / Δ V|.
 *   3. Pick the pair with the largest jump.
 *   4. Linear-interpolate the volume at which pH = 7 within that
 *      interval; if the interval does not bracket 7, return the pair
 *      midpoint with its observed pH.
 *
 * Threshold: at least 0.5 pH units per mL — keeps drift through
 * neutral on a flat curve from being mis-flagged as an endpoint.
 */
export function detectEquivalencePoint(samples) {
    // Compact to monotonic-volume non-null pairs.
    const pts = [];
    for (const s of samples) {
        if (s.pH === null)
            continue;
        if (pts.length > 0 && s.volumeMl <= pts[pts.length - 1].v)
            continue;
        pts.push({ v: s.volumeMl, pH: s.pH });
    }
    if (pts.length < 2)
        return null;
    let best = null;
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        const dV = b.v - a.v;
        if (dV <= 0)
            continue;
        const jump = Math.abs(b.pH - a.pH) / dV;
        if (jump < 0.5)
            continue;
        if (!best || jump > best.jump)
            best = { i, jump };
    }
    if (!best)
        return null;
    const a = pts[best.i - 1];
    const b = pts[best.i];
    // Bracket pH = 7? Linear-interp the crossing volume.
    if ((a.pH - 7) * (b.pH - 7) <= 0 && a.pH !== b.pH) {
        const f = (7 - a.pH) / (b.pH - a.pH);
        const v = a.v + f * (b.v - a.v);
        return { volumeMl: v, pH: 7, jump: best.jump };
    }
    // Otherwise: pair midpoint + average pH.
    return {
        volumeMl: (a.v + b.v) / 2,
        pH: (a.pH + b.pH) / 2,
        jump: best.jump
    };
}
/**
 * Auto-scale the X-axis maximum for the curve overlay. Rounds up
 * to the next 5 mL grid so the grid lines stay readable.
 */
export function autoScaleVolumeMax(samples) {
    let max = 0;
    for (const s of samples)
        if (s.volumeMl > max)
            max = s.volumeMl;
    if (max <= 50)
        return 50;
    return Math.ceil(max / 5) * 5;
}
/**
 * Decide whether the active scenario is a titration. Heuristic:
 * the scenario id contains "titration" (case-insensitive). Stage
 * has no other reliable signal exposed at the inspection layer,
 * and the curriculum naming convention is consistent.
 */
export function isTitrationScenarioId(id) {
    if (!id)
        return false;
    return id.toLowerCase().includes("titration");
}
